/**
 * Global Privacy Control (GPC) Application Module
 * 
 * This module applies GPC opt-out preferences to US state privacy sections.
 * When GPC is enabled, it automatically sets opt-out flags for data sales,
 * sharing, and targeted advertising according to each state's privacy law.
 * 
 * Supported states: California (CA), Colorado (CO), Connecticut (CT), Utah (UT), Virginia (VA)
 */

import {
  UsCa as UsCaV1,
  UsCo as UsCoV1,
  UsCt as UsCtV1,
  UsDe as UsDeV1,
  UsIa as UsIaV1,
  UsMt as UsMtV1,
  UsNe as UsNeV1,
  UsNh as UsNhV1,
  UsNj as UsNjV1,
  UsOr as UsOrV1,
  UsTn as UsTnV1,
  UsTx as UsTxV1,
  UsUt as UsUtV1,
  UsVa as UsVaV1,
} from '@iabgpp/cmpapi';
import { ampLogInfo } from './util';

/**
 * Helper function to safely set a field value on the CMP API.
 * 
 * This function tries to set the field value using both boolean and numeric (0/1) values
 * because different versions of the IAB GPP library may expect different types.
 * 
 * @param cmpApi - The CMP API instance
 * @param sectionName - The section name (e.g., 'usca', 'usco')
 * @param fieldName - The field name to set
 * @param value - The value to set (will try both boolean and numeric)
 */
function safeSetFieldValue(cmpApi: any, sectionName: string, fieldName: string, value: boolean | number): void {
  try {
    // Try setting the value as-is first
    cmpApi.setFieldValue(sectionName, fieldName, value);
  } catch (error) {
    try {
      // If that fails, try converting boolean to number (true -> 1, false -> 0)
      const numericValue = typeof value === 'boolean' ? (value ? 1 : 0) : value;
      cmpApi.setFieldValue(sectionName, fieldName, numericValue);
    } catch (secondError) {
      // If both attempts fail, log the error but continue
      // This ensures GPC application is best-effort and doesn't break the CMP
      console.warn(`Failed to set ${sectionName}.${fieldName}:`, secondError);
    }
  }
}

/**
 * Applies GPC opt-out preferences to California (CA) privacy section.
 * 
 * California CPRA requires honoring GPC for:
 * - SaleOptOut: Opt-out of sale of personal information
 * - SharingOptOut: Opt-out of sharing for cross-context behavioral advertising
 * 
 * @param cmpApi - The CMP API instance
 */
function applyGpcToCalifornia(cmpApi: any): void {
  ampLogInfo('GPC: Applying opt-out to California (CA) section');
  
  // Set opt-out flags (1 = opted out, 2 = did not opt out)
  // When GPC is enabled, we set to 1 (opted out)
  safeSetFieldValue(cmpApi, UsCaV1.NAME, 'SaleOptOut', 1);
  safeSetFieldValue(cmpApi, UsCaV1.NAME, 'SharingOptOut', 1);
  
  // Set GPC segment to indicate GPC signal was honored
  safeSetFieldValue(cmpApi, UsCaV1.NAME, 'GpcSegmentIncluded', 1);
}

/**
 * Applies GPC opt-out preferences to Colorado (CO) privacy section.
 * 
 * Colorado CPA requires honoring GPC for:
 * - SaleOptOut: Opt-out of sale of personal information
 * - TargetedAdvertisingOptOut: Opt-out of targeted advertising
 * 
 * Note: Colorado does not have a separate "SharingOptOut" field
 * 
 * @param cmpApi - The CMP API instance
 */
function applyGpcToColorado(cmpApi: any): void {
  ampLogInfo('GPC: Applying opt-out to Colorado (CO) section');
  
  // Set opt-out flags (1 = opted out, 2 = did not opt out)
  safeSetFieldValue(cmpApi, UsCoV1.NAME, 'SaleOptOut', 1);
  safeSetFieldValue(cmpApi, UsCoV1.NAME, 'TargetedAdvertisingOptOut', 1);
  
  // Set GPC segment to indicate GPC signal was honored
  safeSetFieldValue(cmpApi, UsCoV1.NAME, 'GpcSegmentIncluded', 1);
}

/**
 * Applies GPC opt-out preferences to Connecticut (CT) privacy section.
 * 
 * Connecticut CTDPA requires honoring GPC for:
 * - SaleOptOut: Opt-out of sale of personal information
 * - TargetedAdvertisingOptOut: Opt-out of targeted advertising
 * 
 * @param cmpApi - The CMP API instance
 */
function applyGpcToConnecticut(cmpApi: any): void {
  ampLogInfo('GPC: Applying opt-out to Connecticut (CT) section');
  
  // Set opt-out flags (1 = opted out, 2 = did not opt out)
  safeSetFieldValue(cmpApi, UsCtV1.NAME, 'SaleOptOut', 1);
  safeSetFieldValue(cmpApi, UsCtV1.NAME, 'TargetedAdvertisingOptOut', 1);
  
  // Set GPC segment to indicate GPC signal was honored
  safeSetFieldValue(cmpApi, UsCtV1.NAME, 'GpcSegmentIncluded', 1);
}

/**
 * Applies GPC opt-out preferences to Utah (UT) privacy section.
 * 
 * Utah UCPA requires honoring GPC for:
 * - SaleOptOut: Opt-out of sale of personal information
 * - TargetedAdvertisingOptOut: Opt-out of targeted advertising
 * 
 * Note: Utah does not have GPC-specific fields in the GPP spec
 * 
 * @param cmpApi - The CMP API instance
 */
function applyGpcToUtah(cmpApi: any): void {
  ampLogInfo('GPC: Applying opt-out to Utah (UT) section');
  
  // Set opt-out flags (1 = opted out, 2 = did not opt out)
  safeSetFieldValue(cmpApi, UsUtV1.NAME, 'SaleOptOut', 1);
  safeSetFieldValue(cmpApi, UsUtV1.NAME, 'TargetedAdvertisingOptOut', 1);
  
  // Note: Utah spec does not include GpcSegmentIncluded field
}

/**
 * Applies GPC opt-out preferences to Virginia (VA) privacy section.
 * 
 * Virginia VCDPA requires honoring GPC for:
 * - SaleOptOut: Opt-out of sale of personal information
 * - TargetedAdvertisingOptOut: Opt-out of targeted advertising
 * 
 * Note: Virginia does not have GPC-specific fields in the GPP spec
 * 
 * @param cmpApi - The CMP API instance
 */
function applyGpcToVirginia(cmpApi: any): void {
  ampLogInfo('GPC: Applying opt-out to Virginia (VA) section');
  
  // Set opt-out flags (1 = opted out, 2 = did not opt out)
  safeSetFieldValue(cmpApi, UsVaV1.NAME, 'SaleOptOut', 1);
  safeSetFieldValue(cmpApi, UsVaV1.NAME, 'TargetedAdvertisingOptOut', 1);
  
  // Note: Virginia spec does not include GpcSegmentIncluded field
}

function applyGpcToDelaware(cmpApi: any): void {
  ampLogInfo('GPC: Applying opt-out to Delaware (DE) section');

  safeSetFieldValue(cmpApi, UsDeV1.NAME, 'SaleOptOut', 1);
  safeSetFieldValue(cmpApi, UsDeV1.NAME, 'TargetedAdvertisingOptOut', 1);
  safeSetFieldValue(cmpApi, UsDeV1.NAME, 'GpcSegmentIncluded', 1);
  safeSetFieldValue(cmpApi, UsDeV1.NAME, 'Gpc', 1);
}

function applyGpcToIowa(cmpApi: any): void {
  ampLogInfo('GPC: Applying opt-out to Iowa (IA) section');

  safeSetFieldValue(cmpApi, UsIaV1.NAME, 'SaleOptOut', 1);
  safeSetFieldValue(cmpApi, UsIaV1.NAME, 'TargetedAdvertisingOptOut', 1);
  safeSetFieldValue(cmpApi, UsIaV1.NAME, 'GpcSegmentIncluded', 1);
  safeSetFieldValue(cmpApi, UsIaV1.NAME, 'Gpc', 1);
}

function applyGpcToMontana(cmpApi: any): void {
  ampLogInfo('GPC: Applying opt-out to Montana (MT) section');

  safeSetFieldValue(cmpApi, UsMtV1.NAME, 'SaleOptOut', 1);
  safeSetFieldValue(cmpApi, UsMtV1.NAME, 'TargetedAdvertisingOptOut', 1);
  safeSetFieldValue(cmpApi, UsMtV1.NAME, 'GpcSegmentIncluded', 1);
  safeSetFieldValue(cmpApi, UsMtV1.NAME, 'Gpc', 1);
}

function applyGpcToNebraska(cmpApi: any): void {
  ampLogInfo('GPC: Applying opt-out to Nebraska (NE) section');

  safeSetFieldValue(cmpApi, UsNeV1.NAME, 'SaleOptOut', 1);
  safeSetFieldValue(cmpApi, UsNeV1.NAME, 'TargetedAdvertisingOptOut', 1);
  safeSetFieldValue(cmpApi, UsNeV1.NAME, 'GpcSegmentIncluded', 1);
  safeSetFieldValue(cmpApi, UsNeV1.NAME, 'Gpc', 1);
}

function applyGpcToNewHampshire(cmpApi: any): void {
  ampLogInfo('GPC: Applying opt-out to New Hampshire (NH) section');

  safeSetFieldValue(cmpApi, UsNhV1.NAME, 'SaleOptOut', 1);
  safeSetFieldValue(cmpApi, UsNhV1.NAME, 'TargetedAdvertisingOptOut', 1);
  safeSetFieldValue(cmpApi, UsNhV1.NAME, 'GpcSegmentIncluded', 1);
  safeSetFieldValue(cmpApi, UsNhV1.NAME, 'Gpc', 1);
}

function applyGpcToNewJersey(cmpApi: any): void {
  ampLogInfo('GPC: Applying opt-out to New Jersey (NJ) section');

  safeSetFieldValue(cmpApi, UsNjV1.NAME, 'SaleOptOut', 1);
  safeSetFieldValue(cmpApi, UsNjV1.NAME, 'TargetedAdvertisingOptOut', 1);
  safeSetFieldValue(cmpApi, UsNjV1.NAME, 'GpcSegmentIncluded', 1);
  safeSetFieldValue(cmpApi, UsNjV1.NAME, 'Gpc', 1);
}

function applyGpcToOregon(cmpApi: any): void {
  ampLogInfo('GPC: Applying opt-out to Oregon (OR) section');

  safeSetFieldValue(cmpApi, UsOrV1.NAME, 'SaleOptOut', 1);
  safeSetFieldValue(cmpApi, UsOrV1.NAME, 'TargetedAdvertisingOptOut', 1);
  safeSetFieldValue(cmpApi, UsOrV1.NAME, 'GpcSegmentIncluded', 1);
  safeSetFieldValue(cmpApi, UsOrV1.NAME, 'Gpc', 1);
}

function applyGpcToTennessee(cmpApi: any): void {
  ampLogInfo('GPC: Applying opt-out to Tennessee (TN) section');

  safeSetFieldValue(cmpApi, UsTnV1.NAME, 'SaleOptOut', 1);
  safeSetFieldValue(cmpApi, UsTnV1.NAME, 'TargetedAdvertisingOptOut', 1);
  safeSetFieldValue(cmpApi, UsTnV1.NAME, 'GpcSegmentIncluded', 1);
  safeSetFieldValue(cmpApi, UsTnV1.NAME, 'Gpc', 1);
}

function applyGpcToTexas(cmpApi: any): void {
  ampLogInfo('GPC: Applying opt-out to Texas (TX) section');

  safeSetFieldValue(cmpApi, UsTxV1.NAME, 'SaleOptOut', 1);
  safeSetFieldValue(cmpApi, UsTxV1.NAME, 'TargetedAdvertisingOptOut', 1);
  safeSetFieldValue(cmpApi, UsTxV1.NAME, 'GpcSegmentIncluded', 1);
  safeSetFieldValue(cmpApi, UsTxV1.NAME, 'Gpc', 1);
}

/**
 * Applies GPC opt-out preferences to the appropriate US state privacy section.
 * 
 * This is the main entry point for applying GPC. It determines which state's
 * privacy section to update based on the region parameter and applies the
 * appropriate opt-out settings.
 * 
 * When GPC is enabled, this function:
 * 1. Sets SaleOptOut = 1 (opted out)
 * 2. Sets SharingOptOut = 1 (only for CA, where supported)
 * 3. Sets TargetedAdvertisingOptOut = 1 (for states that support it)
 * 4. Sets GpcSegmentIncluded = 1 (for states that support it: CA, CO, CT)
 * 
 * @param cmpApi - The CMP API instance
 * @param region - The US state code (e.g., 'CA', 'CO', 'CT', 'UT', 'VA')
 */
export function applyGpcToUsSections(cmpApi: any, region: string): void {
  if (!cmpApi) {
    console.error('GPC: Cannot apply GPC - cmpApi is null or undefined');
    return;
  }

  if (!region) {
    console.warn('GPC: Cannot apply GPC - region is null or undefined');
    return;
  }

  ampLogInfo(`GPC: Applying Global Privacy Control opt-out for region: ${region}`);

  // Apply GPC based on the user's state
  switch (region.toUpperCase()) {
    case 'CA':
      applyGpcToCalifornia(cmpApi);
      break;
    
    case 'CO':
      applyGpcToColorado(cmpApi);
      break;
    
    case 'CT':
      applyGpcToConnecticut(cmpApi);
      break;

    case 'DE':
      applyGpcToDelaware(cmpApi);
      break;

    case 'IA':
      applyGpcToIowa(cmpApi);
      break;

    case 'MT':
      applyGpcToMontana(cmpApi);
      break;

    case 'NE':
      applyGpcToNebraska(cmpApi);
      break;

    case 'NH':
      applyGpcToNewHampshire(cmpApi);
      break;

    case 'NJ':
      applyGpcToNewJersey(cmpApi);
      break;

    case 'OR':
      applyGpcToOregon(cmpApi);
      break;

    case 'TN':
      applyGpcToTennessee(cmpApi);
      break;

    case 'TX':
      applyGpcToTexas(cmpApi);
      break;
    
    case 'UT':
      applyGpcToUtah(cmpApi);
      break;
    
    case 'VA':
      applyGpcToVirginia(cmpApi);
      break;
    
    default:
      // For other US states that don't have specific privacy laws yet,
      // we don't apply GPC as there's no applicable section
      ampLogInfo(`GPC: No applicable privacy section for region: ${region}`);
      break;
  }
}
